import { useEffect, useRef, useCallback } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CheckCircle2, Shield, BookOpen, Heart, Activity, Brain, Zap, Lock, CreditCard, ChevronRight, ArrowRight, Microscope, Target, BarChart3, Star, type LucideIcon } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { IMAGES } from '@/assets/images';
import { COURSE_MODULES, FAQ_ITEMS, ONCO_TREINO_MODULES, CHECKOUT_URL, ROUTE_PATHS, PAIN_POINTS, LEARNING_ITEMS, type LearningItem } from '@/lib/index';
function Reveal({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 28
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay,
    ease: [0.22, 1, 0.36, 1]
  }} className={className}>
      {children}
    </motion.div>;
}
function MedicalGrid() {
  return <svg className="absolute inset-0 w-full h-full opacity-[0.035] pointer-events-none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
          <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#a78bfa" strokeWidth="0.6" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#grid)" />
    </svg>;
}
function LearningCarousel({
  items
}: {
  items: LearningItem[];
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<number>(0);
  const posRef = useRef(0);
  const velRef = useRef(0);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartPosRef = useRef(0);
  const lastDragXRef = useRef(0);
  const tripled = [...items, ...items, ...items];
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const animate = () => {
      const half = track.scrollWidth / 3;
      if (!isDraggingRef.current) {
        if (Math.abs(velRef.current) > 0.05) {
          posRef.current += velRef.current;
          velRef.current *= 0.92;
        } else {
          velRef.current = 0;
          posRef.current += 0.5;
        }
      }
      if (posRef.current >= half * 2) posRef.current -= half;
      if (posRef.current < 0) posRef.current += half;
      track.style.transform = `translateX(-${posRef.current}px)`;
      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animRef.current);
  }, []);
  const onMouseDown = (e: React.MouseEvent) => {
    isDraggingRef.current = true;
    dragStartXRef.current = e.clientX;
    dragStartPosRef.current = posRef.current;
    lastDragXRef.current = e.clientX;
    velRef.current = 0;
    if (trackRef.current) trackRef.current.style.cursor = 'grabbing';
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDraggingRef.current) return;
    const delta = dragStartXRef.current - e.clientX;
    posRef.current = dragStartPosRef.current + delta;
    velRef.current = lastDragXRef.current - e.clientX;
    lastDragXRef.current = e.clientX;
  };
  const onMouseUp = () => {
    isDraggingRef.current = false;
    if (trackRef.current) trackRef.current.style.cursor = 'grab';
  };
  const onTouchStart = (e: React.TouchEvent) => {
    isDraggingRef.current = true;
    dragStartXRef.current = e.touches[0].clientX;
    dragStartPosRef.current = posRef.current;
    lastDragXRef.current = e.touches[0].clientX;
    velRef.current = 0;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!isDraggingRef.current) return;
    const delta = dragStartXRef.current - e.touches[0].clientX;
    posRef.current = dragStartPosRef.current + delta;
    velRef.current = lastDragXRef.current - e.touches[0].clientX;
    lastDragXRef.current = e.touches[0].clientX;
  };
  const onTouchEnd = () => {
    isDraggingRef.current = false;
  };
  const carouselIcons: LucideIcon[] = [Microscope, Brain, BookOpen, Activity, Shield, Heart, Target, BarChart3, Zap, CheckCircle2];
  return <div className="relative overflow-hidden select-none" style={{
    maskImage: 'linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%)'
  }}>
      <div ref={trackRef} style={{
      display: 'flex',
      gap: 22,
      width: 'max-content',
      willChange: 'transform',
      cursor: 'grab',
      paddingBottom: 12
    }} onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
        {tripled.map((item, idx) => {
        const Icon = carouselIcons[idx % carouselIcons.length];
        const itemNum = idx % items.length + 1;
        const total = items.length;
        const progress = itemNum / total;
        return <div key={`${idx}`} style={{
          width: 338,
          flexShrink: 0,
          borderRadius: 20,
          background: 'linear-gradient(145deg, #110e1f 0%, #0c0916 100%)',
          border: '1px solid rgba(139,92,246,0.28)',
          boxShadow: '0 2px 24px rgba(0,0,0,0.5), inset 0 0 0 1px rgba(139,92,246,0.06)',
          display: 'flex',
          flexDirection: 'column',
          padding: '26px 24px 0',
          transition: 'border-color 0.25s, box-shadow 0.25s',
          cursor: 'default',
          overflow: 'hidden'
        }} onMouseEnter={e => {
          const el = e.currentTarget as HTMLDivElement;
          el.style.borderColor = 'rgba(139,92,246,0.55)';
          el.style.boxShadow = '0 0 28px rgba(139,92,246,0.22), 0 4px 30px rgba(0,0,0,0.6)';
        }} onMouseLeave={e => {
          const el = e.currentTarget as HTMLDivElement;
          el.style.borderColor = 'rgba(139,92,246,0.28)';
          el.style.boxShadow = '0 2px 24px rgba(0,0,0,0.5), inset 0 0 0 1px rgba(139,92,246,0.06)';
        }}>
              {/* Ícone circular com glow */}
              <div style={{
            width: 52,
            height: 52,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(139,92,246,0.35) 0%, rgba(167,139,250,0.15) 100%)',
            border: '1.5px solid rgba(139,92,246,0.5)',
            boxShadow: '0 0 16px rgba(139,92,246,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 16,
            flexShrink: 0
          }}>
                <Icon size={24} style={{
              color: '#a78bfa'
            }} />
              </div>

              {/* Número de progresso */}
              <div style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 500,
            fontSize: '0.78rem',
            color: '#7a6e9a',
            letterSpacing: '0.06em',
            marginBottom: 10
          }}>
                {String(itemNum).padStart(2, '0')} / {String(total).padStart(2, '0')}
              </div>

              {/* Título */}
              <h3 style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 700,
            fontSize: '1.02rem',
            color: '#ffffff',
            marginBottom: 10,
            lineHeight: 1.35
          }}>
                {item.title}
              </h3>

              {/* Descrição */}
              <p style={{
            fontSize: '0.84rem',
            color: '#8b82a8',
            lineHeight: 1.6,
            flexGrow: 1,
            marginBottom: 20
          }}>
                {item.description}
              </p>

              {/* Barra de progresso bicolor na base */}
              <div style={{
            height: 3,
            borderRadius: '0 0 0 0',
            marginLeft: -24,
            marginRight: -24,
            display: 'flex',
            overflow: 'hidden'
          }}>
                <div style={{
              width: `${progress * 100}%`,
              background: 'linear-gradient(90deg, #8b5cf6, #a78bfa)',
              boxShadow: '0 0 8px rgba(139,92,246,0.6)',
              transition: 'width 0.3s'
            }} />
                <div style={{
              flex: 1,
              background: 'rgba(139,92,246,0.12)'
            }} />
              </div>
            </div>;
      })}
      </div>
    </div>;
}
export default function Home() {
  useEffect(() => {
    document.documentElement.style.background = '#07050f';
    document.body.style.background = '#07050f';
  }, []);
  const openCheckout = useCallback(() => window.open(CHECKOUT_URL, '_blank'), []);
  const moduleIcons: LucideIcon[] = [Microscope, Shield, Activity, Heart, Target, BarChart3];
  const scrollTo = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }, []);
  return <div style={{
    background: '#07050f',
    color: '#f0eeff',
    fontFamily: "'Inter', sans-serif"
  }} className="min-h-screen overflow-x-hidden">
      <header className="fixed top-0 left-0 right-0 z-50" style={{
      background: 'rgba(7,5,15,0.92)',
      backdropFilter: 'blur(14px)',
      borderBottom: '1px solid rgba(139,92,246,0.15)'
    }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between" style={{
        padding: '12px 16px'
      }}>
          <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10
        }}>
            <Brain size={22} style={{
            color: '#a78bfa'
          }} />
            <div style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 700,
            fontSize: 'clamp(0.95rem, 3vw, 1.25rem)',
            color: '#f0eeff'
          }}>
              Oncologia em Foco
            </div>
          </div>

          <button className="btn-cta-gold" style={{
          width: 'auto',
          padding: '10px 22px',
          fontSize: '0.87rem'
        }} onClick={openCheckout}>
            Começar Agora
          </button>
        </div>
      </header>

      <div style={{
      height: 60
    }} />

      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{
      background: '#07050f'
    }}>
        <MedicalGrid />
        <div className="relative z-10 max-w-6xl mx-auto w-full px-4 sm:px-6 py-20 sm:py-28 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <motion.div initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5
            }}>
                <div className="section-tag" style={{
                marginBottom: 16
              }}>
                  Mini Curso Online
                </div>
              </motion.div>

              <motion.h1 initial={{
              opacity: 0,
              y: 24
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.7,
              delay: 0.1,
              ease: [0.22, 1, 0.36, 1]
            }} style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 800,
              lineHeight: 1.08,
              letterSpacing: '-0.03em'
            }} className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-6">
                Domine a Oncologia
                <br />
                <span style={{
                color: '#ffffff'
              }}>sem Mistério, com </span>
                <span className="text-glow-violet">Segurança e Autoridade</span>
              </motion.h1>

              <motion.p initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.25
            }} style={{
              color: '#7a6e9a',
              fontSize: '1.15rem',
              lineHeight: 1.7,
              marginBottom: 32
            }}>
                Treinamento prático, direto e completo para{' '}
                <strong style={{
                color: '#f0eeff'
              }}>
                  enfermeiros, técnicos e estudantes
                </strong>{' '}
                que desejam compreender a oncologia com base científica, segurança
                assistencial e visão humanizada.
              </motion.p>

              <motion.ul initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.35
            }} className="space-y-3 mb-10">
                {['6 aulas completas do básico ao avançado', 'Acesso imediato e certificado digital', 'OncoTreino IA: 102 casos + 90 questões', 'Garantia incondicional de 7 dias'].map(item => <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 size={18} style={{
                  color: '#a78bfa',
                  flexShrink: 0,
                  marginTop: 2
                }} />
                    <span style={{
                  color: '#f0eeff',
                  lineHeight: 1.6
                }}>{item}</span>
                  </li>)}
              </motion.ul>

              <motion.div initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.45
            }}>
                <button className="btn-cta-gold text-base mb-4" onClick={openCheckout}>
                  <Zap size={18} />
                  Quero Começar Minha Jornada na Oncologia
                </button>
                <div style={{
                color: '#7a6e9a',
                fontSize: '0.82rem',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}>
                  <Lock size={12} />
                  Pagamento seguro · Acesso imediato · Garantia de 7 dias
                </div>
              </motion.div>
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 40,
            scale: 0.95
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} transition={{
            duration: 0.9,
            delay: 0.5,
            ease: [0.22, 1, 0.36, 1]
          }} className="relative">
              <div style={{
              position: 'absolute',
              bottom: -40,
              left: '10%',
              right: '10%',
              height: 120,
              borderRadius: '50%',
              background: 'radial-gradient(ellipse, rgba(139,92,246,0.35) 0%, transparent 70%)',
              filter: 'blur(20px)',
              zIndex: 0
            }} />
              <img src="https://static-us-img.skywork.ai/prod/user/head_picture/2052828667291283456_WhatsApp Image 2026-05-08 at 16.09.48.jpeg?image_process=quality,q_90/resize,w_1280/format,webp" alt="Mini Curso Oncologia em Foco — Produto Digital" style={{
              width: '100%',
              height: 'auto',
              maxHeight: 520,
              objectFit: 'contain',
              borderRadius: 20,
              position: 'relative',
              zIndex: 1,
              boxShadow: '0 0 0 1px rgba(139,92,246,0.15), 0 30px 80px rgba(0,0,0,0.8)'
            }} className="h-[519.984375px] object-cover" />
            </motion.div>
          </div>
        </div>

        <div className="line-glow absolute bottom-0 left-0 right-0" />
      </section>

      <section className="section-alt relative py-20 sm:py-28 overflow-hidden">
        <MedicalGrid />
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6">
          <Reveal className="text-center mb-16">
            <div className="section-tag" style={{
            margin: '0 auto 16px',
            display: 'inline-flex'
          }}>
              Para quem é este curso
            </div>
            <h2 style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 800,
            fontSize: 'clamp(1.8rem, 3vw, 2.6rem)',
            color: '#ffffff',
            marginBottom: 16,
            letterSpacing: '-0.02em'
          }}>
              Este Curso Foi Feito{' '}
              <span className="text-glow-violet">Especialmente Para Você</span>
            </h2>
          </Reveal>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5">
            {[{
            icon: Heart,
            title: 'Enfermeiros',
            desc: 'Que desejam iniciar ou se especializar em oncologia com segurança técnica'
          }, {
            icon: Activity,
            title: 'Técnicos',
            desc: 'Que buscam compreender o cuidado oncológico e atuar com mais confiança'
          }, {
            icon: BookOpen,
            title: 'Estudantes',
            desc: 'Que querem se preparar para atuar na área com base científica sólida'
          }].map(item => <Reveal key={item.title}>
                <div className="card-premium p-6 text-center">
                  <div style={{
                width: 56,
                height: 56,
                borderRadius: 16,
                background: 'linear-gradient(135deg, rgba(139,92,246,0.25) 0%, rgba(167,139,250,0.12) 100%)',
                border: '1px solid rgba(139,92,246,0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                boxShadow: '0 0 18px rgba(139,92,246,0.2)'
              }}>
                    <item.icon size={26} style={{
                  color: '#a78bfa'
                }} />
                  </div>
                  <h3 style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 700,
                color: '#ffffff',
                fontSize: '1.1rem',
                marginBottom: 8
              }}>
                    {item.title}
                  </h3>
                  <p style={{
                color: '#7a6e9a',
                fontSize: '0.88rem',
                lineHeight: 1.65
              }}>
                    {item.desc}
                  </p>
                </div>
              </Reveal>)}
          </div>
        </div>
        <div className="line-glow absolute bottom-0 left-0 right-0" />
      </section>

      <section id="problemas" className="relative py-20 sm:py-28 overflow-hidden" style={{
      background: '#07050f'
    }}>
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal className="text-center mb-16">
            <div className="section-tag" style={{
            margin: '0 auto 16px',
            display: 'inline-flex'
          }}>
              Reconheça seu cenário atual
            </div>
            <h2 style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 800,
            fontSize: 'clamp(1.8rem, 3vw, 2.7rem)',
            color: '#ffffff',
            letterSpacing: '-0.02em',
            marginBottom: 16
          }}>
              Você Já Se Sentiu Assim
              <br />
              <span style={{
              color: '#ef4444',
              textShadow: '0 0 20px rgba(239,68,68,0.35)'
            }}>
                Trabalhando em Oncologia?
              </span>
            </h2>
            <p style={{
            color: '#7a6e9a',
            maxWidth: 560,
            margin: '0 auto',
            lineHeight: 1.7
          }}>
              Essas inseguranças são mais comuns do que você imagina — e têm solução.
            </p>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {PAIN_POINTS.map(pain => <Reveal key={pain.id}>
                <div className="p-6" style={{
              background: 'linear-gradient(145deg, #100d1c 0%, #0b0814 100%)',
              border: '1px solid rgba(239,68,68,0.2)',
              borderRadius: 16
            }}>
                  <div className="flex items-start gap-4">
                    <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: 'rgba(239,68,68,0.12)',
                  border: '1px solid rgba(239,68,68,0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontSize: '1.2rem'
                }}>
                      {pain.icon}
                    </div>
                    <div>
                      <h3 style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 700,
                    color: '#ffffff',
                    fontSize: '1rem',
                    marginBottom: 6
                  }}>
                        {pain.title}
                      </h3>
                      <p style={{
                    color: '#7a6e9a',
                    fontSize: '0.88rem',
                    lineHeight: 1.65
                  }}>
                        {pain.desc}
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>)}
          </div>
        </div>
        <div className="line-glow absolute bottom-0 left-0 right-0" />
      </section>

      <section className="section-alt relative py-20 sm:py-28 overflow-hidden">
        <MedicalGrid />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Reveal>
            <div className="section-tag" style={{
            margin: '0 auto 16px',
            display: 'inline-flex'
          }}>
              A transformação que você vai ter
            </div>
            <h2 style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 800,
            fontSize: 'clamp(1.9rem, 3.5vw, 2.8rem)',
            color: '#ffffff',
            letterSpacing: '-0.02em',
            marginBottom: 24
          }}>
              Ao Concluir o Curso, Você Vai Ter
              <br />
              <span className="text-glow-violet">
                Uma Visão Completa da Oncologia Moderna
              </span>
            </h2>
            <p style={{
            color: '#7a6e9a',
            fontSize: '1.1rem',
            lineHeight: 1.75,
            marginBottom: 48,
            maxWidth: 700,
            margin: '0 auto 48px'
          }}>
              O <strong style={{
              color: '#a78bfa'
            }}>Mini Curso Oncologia em Foco</strong>{' '}
              une base científica, prática clínica, segurança assistencial, gestão e
              humanização — tudo que você precisa para atuar com confiança.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-left max-w-2xl mx-auto">
              {['Fundamentos da oncologia e equipe multidisciplinar', 'Quimioterapia: da teoria à administração segura', 'Radioterapia e cirurgia oncológica na prática', 'Cuidados paliativos com ética e humanização', 'Gestão da qualidade e farmacoeconomia', 'OncoTreino IA: 102 casos + 90 questões'].map(item => <div key={item} className="flex items-center gap-3" style={{
              padding: '14px 18px',
              borderRadius: 12,
              background: 'rgba(139,92,246,0.04)',
              border: '1px solid rgba(139,92,246,0.1)'
            }}>
                  <ChevronRight size={16} style={{
                color: '#a78bfa',
                flexShrink: 0
              }} />
                  <span style={{
                color: '#f0eeff',
                fontSize: '0.92rem'
              }}>{item}</span>
                </div>)}
            </div>
          </Reveal>
        </div>
        <div className="line-glow absolute bottom-0 left-0 right-0" />
      </section>

      <section id="conteudo" className="relative py-20 sm:py-28 overflow-hidden" style={{
      background: '#07050f'
    }}>
        <div className="relative z-10">
          <Reveal className="text-center mb-16 px-6">
            <div className="section-tag" style={{
            margin: '0 auto 16px',
            display: 'inline-flex'
          }}>
              Conteúdo programático
            </div>
            <h2 style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 800,
            fontSize: 'clamp(1.8rem, 3vw, 2.7rem)',
            color: '#ffffff',
            letterSpacing: '-0.02em',
            marginBottom: 12
          }}>
              Tudo o Que Você Vai <span className="text-glow-violet">Aprender</span>
            </h2>
            <p style={{
            color: '#7a6e9a',
            maxWidth: 520,
            margin: '0 auto'
          }}>
              Conteúdo estruturado do básico ao aplicado — com profundidade técnica e
              linguagem acessível
            </p>
          </Reveal>

          <LearningCarousel items={LEARNING_ITEMS} />
        </div>
        <div className="line-glow absolute bottom-0 left-0 right-0" />
      </section>

      <section className="section-alt relative py-20 sm:py-28 overflow-hidden">
        <MedicalGrid />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6">
          <Reveal className="text-center mb-16">
            <div className="section-tag" style={{
            margin: '0 auto 16px',
            display: 'inline-flex'
          }}>
              Sobre o Criador
            </div>
            <h2 style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 800,
            fontSize: 'clamp(1.8rem, 3vw, 2.7rem)',
            color: '#ffffff',
            letterSpacing: '-0.02em',
            marginBottom: 12
          }}>
              Quem está <span className="text-glow-violet">por trás do curso?</span>
            </h2>
          </Reveal>

          <Reveal>
            <div style={{
            background: 'linear-gradient(145deg, #100d1c 0%, #0b0814 100%)',
            border: '1px solid rgba(139,92,246,0.25)',
            borderRadius: 24,
            padding: 'clamp(28px, 6vw, 52px)',
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: 'clamp(24px, 5vw, 48px)',
            alignItems: 'start'
          }} className="flex-col sm:grid">
              {/* Foto / ícone */}
              <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
              minWidth: 140
            }}>
              <div style={{
                width: 160,
                height: 160,
                borderRadius: '50%',
                border: '3px solid rgba(139,92,246,0.6)',
                boxShadow: '0 0 0 6px rgba(139,92,246,0.12), 0 0 40px rgba(139,92,246,0.25)',
                flexShrink: 0,
                overflow: 'hidden'
              }}>
                  <img src={IMAGES.FOTO_CRIADOR} alt="Enf. Artur Cunha" style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center top'
                }} />
                </div>
                <div style={{
                textAlign: 'center'
              }}>
                  <div style={{
                  color: '#a78bfa',
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase'
                }}>
                    Enfermeiro Oncologista
                  </div>
                </div>
              </div>

              {/* Texto */}
              <div>
                <h3 style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 800,
                fontSize: 'clamp(1.3rem, 3vw, 1.8rem)',
                color: '#ffffff',
                marginBottom: 8,
                lineHeight: 1.2
              }}>
                  Enf. Artur Cunha
                </h3>
                <p style={{
                color: '#c4b5fd',
                fontSize: '0.95rem',
                lineHeight: 1.7,
                marginBottom: 24
              }}>
                  Enfermeiro Oncologista com formação sólida e mais de 11 anos de experiência prática em oncologia, especializado em quimioterapia, emergências oncológicas e gestão de cuidados de alta complexidade.
                </p>

                <div className="space-y-3">
                  {['Bacharel e Licenciado em Enfermagem pela Faculdade de Medicina da UFU', 'Especialista (Residente) em Atenção em Oncologia — FAMED/UFU', 'Especialização em Docência do Ensino Superior — Universidade Cândido Mendes', 'Especialização em Auditoria em Serviços de Saúde — Faculdade Holística', 'MBA Executivo em Saúde — Universidade Cândido Mendes', 'Curso de Avaliadores do Sistema Brasileiro de Acreditação — ONA (2019)', 'Mais de 9 anos de experiência prática em Oncologia'].map(item => <div key={item} className="flex items-start gap-3">
                      <div style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: '#8b5cf6',
                    marginTop: 8,
                    flexShrink: 0
                  }} />
                      <span style={{
                    color: '#a09cbf',
                    fontSize: '0.88rem',
                    lineHeight: 1.6
                  }}>{item}</span>
                    </div>)}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
        <div className="line-glow absolute bottom-0 left-0 right-0" />
      </section>


      <section id="diferenciais" className="relative py-20 sm:py-28 overflow-hidden" style={{
      background: '#07050f'
    }}>
        <MedicalGrid />
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal className="text-center mb-16">
            <div className="section-tag" style={{
            margin: '0 auto 16px',
            display: 'inline-flex'
          }}>
              Por que escolher
            </div>
            <h2 style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 800,
            fontSize: 'clamp(1.8rem, 3vw, 2.7rem)',
            color: '#ffffff',
            letterSpacing: '-0.02em',
            marginBottom: 12
          }}>
              Por que o Oncologia em Foco é{' '}
              <span className="text-glow-violet">Diferente?</span>
            </h2>
            <p style={{
            color: '#7a6e9a',
            maxWidth: 560,
            margin: '0 auto',
            fontSize: '1.05rem'
          }}>
              Não é mais um curso teórico. É uma formação que constrói raciocínio clínico
              real.
            </p>
          </Reveal>

          <div className="space-y-6">
            <Reveal delay={0.1}>
              <div style={{
              background: 'linear-gradient(145deg, #0e0a1f 0%, #09071a 100%)',
              border: '1px solid rgba(251,191,36,0.25)',
              borderRadius: 20,
              padding: '36px 40px',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 32,
              flexWrap: 'wrap'
            }}>
                <div style={{
                flex: 1,
                minWidth: 280
              }}>
                  <h3 style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: '1.3rem',
                  color: '#ffffff',
                  marginBottom: 14
                }}>
                    💣 Formação Completa
                  </h3>
                  <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8
                }}>
                    {['Fundamentos', 'Tratamentos modernos', 'Cuidado integral', 'Gestão assistencial'].map(tag => <span key={tag} style={{
                    background: 'rgba(139,92,246,0.1)',
                    border: '1px solid rgba(139,92,246,0.25)',
                    color: '#a78bfa',
                    fontSize: '0.75rem',
                    borderRadius: 99,
                    padding: '4px 12px'
                  }}>
                        {tag}
                      </span>)}
                  </div>
                </div>
                <div style={{
                fontStyle: 'italic',
                color: '#7a6e9a',
                fontSize: '1rem'
              }}>
                  Do básico ao avançado — sem lacunas
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {COURSE_MODULES.map((aula, idx) => <div key={aula.id} style={{
                background: '#07050f',
                border: '1px solid rgba(139,92,246,0.15)',
                borderRadius: 14,
                padding: '20px 24px',
                borderLeft: '3px solid #a78bfa',
                transition: 'transform 0.2s',
                cursor: 'default'
              }} onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.01)'} onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
                    <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 12
                }}>
                      <span style={{
                    fontSize: '1.2rem'
                  }}>
                        {['📚', '💉', '☢️', '🏥', '💛', '📊'][idx]}
                      </span>
                      <span style={{
                    background: 'rgba(251,191,36,0.15)',
                    border: '1px solid rgba(251,191,36,0.3)',
                    borderRadius: 6,
                    padding: '2px 8px',
                    fontSize: '0.7rem',
                    color: '#fbbf24',
                    fontWeight: 700
                  }}>
                        {aula.id}
                      </span>
                    </div>
                    <h4 style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  color: '#ffffff',
                  marginBottom: 10
                }}>
                      {aula.title}
                    </h4>
                    <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 4
                }}>
                      {aula.topics.slice(0, 3).map(topic => <span key={topic} style={{
                    color: '#7a6e9a',
                    fontSize: '0.75rem'
                  }}>
                            {topic}
                          </span>).reduce((prev, curr, i) => [...prev, i > 0 && <span key={`sep-${i}`} style={{
                    color: '#7a6e9a',
                    fontSize: '0.75rem'
                  }}>
                                {' · '}
                              </span>, curr], [] as React.ReactNode[])}
                    </div>
                  </div>)}
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div style={{
                background: '#0d0a1a',
                border: '1px solid rgba(251,191,36,0.25)',
                borderLeft: '3px solid #fbbf24',
                borderRadius: 14,
                padding: '24px 28px',
                transition: 'transform 0.2s'
              }} onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.01)'} onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
                  <h4 style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  color: '#ffffff',
                  marginBottom: 14
                }}>
                    🚀 Treina com IA
                  </h4>
                  <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  marginBottom: 16
                }}>
                    {['102 casos clínicos', '90 questões', 'Decisões simuladas', 'Score em tempo real'].map(stat => <span key={stat} style={{
                    background: 'rgba(251,191,36,0.1)',
                    border: '1px solid rgba(251,191,36,0.25)',
                    color: '#fbbf24',
                    fontSize: '0.75rem',
                    borderRadius: 99,
                    padding: '4px 12px'
                  }}>
                        {stat}
                      </span>)}
                  </div>
                  <div style={{
                  background: 'rgba(251,191,36,0.06)',
                  border: '1px solid rgba(251,191,36,0.2)',
                  borderRadius: 12,
                  padding: '10px 16px',
                  marginTop: 16
                }}>
                    <p style={{
                    color: '#fbbf24',
                    fontWeight: 700,
                    fontSize: '0.88rem',
                    margin: 0
                  }}>
                      💣 Acelera o raciocínio clínico
                    </p>
                  </div>
                </div>

                <div style={{
                background: '#0d0a1a',
                border: '1px solid rgba(139,92,246,0.25)',
                borderLeft: '3px solid #a78bfa',
                borderRadius: 14,
                padding: '24px 28px',
                transition: 'transform 0.2s'
              }} onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.01)'} onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
                  <h4 style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  color: '#ffffff',
                  marginBottom: 14
                }}>
                    🚨 Situações Críticas
                  </h4>
                  <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  marginBottom: 16
                }}>
                    {['Anafilaxia', 'Extravasamento', 'Neutropenia febril', 'Reações infusionais'].map(crit => <span key={crit} style={{
                    background: 'rgba(139,92,246,0.1)',
                    border: '1px solid rgba(139,92,246,0.25)',
                    color: '#a78bfa',
                    fontSize: '0.75rem',
                    borderRadius: 99,
                    padding: '4px 12px'
                  }}>
                        {crit}
                      </span>)}
                  </div>
                  <div style={{
                  background: 'rgba(251,191,36,0.06)',
                  border: '1px solid rgba(251,191,36,0.2)',
                  borderRadius: 12,
                  padding: '10px 16px',
                  marginTop: 16
                }}>
                    <p style={{
                    color: '#fbbf24',
                    fontWeight: 700,
                    fontSize: '0.88rem',
                    margin: 0
                  }}>
                      🔥 Aprende a pensar sob pressão
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.25}>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div style={{
                background: '#0d0a1a',
                border: '1px solid rgba(139,92,246,0.25)',
                borderLeft: '3px solid #a78bfa',
                borderRadius: 14,
                padding: '24px 28px',
                transition: 'transform 0.2s'
              }} onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.01)'} onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
                  <h4 style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  color: '#ffffff',
                  marginBottom: 12
                }}>
                    🧮 Ferramentas Clínicas
                  </h4>
                  <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 4,
                  marginBottom: 14
                }}>
                    {['SC Mosteller', 'Clearance Cr', 'ECOG · Karnofsky', 'Cálculo em tempo real'].map(tool => <span key={tool} style={{
                    color: '#7a6e9a',
                    fontSize: '0.75rem'
                  }}>
                          {tool}
                        </span>).reduce((prev, curr, i) => [...prev, i > 0 && <span key={`sep-${i}`} style={{
                    color: '#7a6e9a',
                    fontSize: '0.75rem'
                  }}>
                              {' · '}
                            </span>, curr], [] as React.ReactNode[])}
                  </div>
                  <div style={{
                  background: 'rgba(251,191,36,0.08)',
                  border: '1px solid rgba(251,191,36,0.2)',
                  borderRadius: 8,
                  padding: '8px 12px'
                }}>
                    <p style={{
                    color: '#fbbf24',
                    fontWeight: 700,
                    fontSize: '0.82rem',
                    margin: 0
                  }}>
                      💣 Teoria + prática
                    </p>
                  </div>
                </div>

                <div style={{
                background: '#0d0a1a',
                border: '1px solid rgba(251,191,36,0.25)',
                borderLeft: '3px solid #fbbf24',
                borderRadius: 14,
                padding: '24px 28px',
                transition: 'transform 0.2s'
              }} onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.01)'} onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
                  <h4 style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  color: '#ffffff',
                  marginBottom: 12
                }}>
                    📋 Checklist QT
                  </h4>
                  <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 14
                }}>
                    {['Pré-infusão', 'Durante', 'Pós'].map((step, i) => <div key={step} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}>
                        <span style={{
                      background: 'rgba(139,92,246,0.1)',
                      border: '1px solid rgba(139,92,246,0.25)',
                      color: '#a78bfa',
                      fontSize: '0.72rem',
                      borderRadius: 99,
                      padding: '3px 10px'
                    }}>
                          {step}
                        </span>
                        {i < 2 && <ArrowRight size={12} style={{
                      color: '#7a6e9a'
                    }} />}
                      </div>)}
                  </div>
                  <div style={{
                  background: 'rgba(251,191,36,0.08)',
                  border: '1px solid rgba(251,191,36,0.2)',
                  borderRadius: 8,
                  padding: '8px 12px'
                }}>
                    <p style={{
                    color: '#fbbf24',
                    fontWeight: 700,
                    fontSize: '0.82rem',
                    margin: 0
                  }}>
                      🔥 Mentalidade de segurança
                    </p>
                  </div>
                </div>

                <div style={{
                background: '#0d0a1a',
                border: '1px solid rgba(139,92,246,0.25)',
                borderLeft: '3px solid #a78bfa',
                borderRadius: 14,
                padding: '24px 28px',
                transition: 'transform 0.2s'
              }} onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.01)'} onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
                  <h4 style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  color: '#ffffff',
                  marginBottom: 12
                }}>
                    🧭 Jornada do Paciente
                  </h4>
                  <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 4,
                  marginBottom: 14
                }}>
                    {['Fluxo oncológico', 'Barreiras de acesso', 'Navegação clínica'].map(nav => <span key={nav} style={{
                    color: '#7a6e9a',
                    fontSize: '0.75rem'
                  }}>
                          {nav}
                        </span>).reduce((prev, curr, i) => [...prev, i > 0 && <span key={`sep-${i}`} style={{
                    color: '#7a6e9a',
                    fontSize: '0.75rem'
                  }}>
                              {' · '}
                            </span>, curr], [] as React.ReactNode[])}
                  </div>
                  <div style={{
                  background: 'rgba(251,191,36,0.08)',
                  border: '1px solid rgba(251,191,36,0.2)',
                  borderRadius: 8,
                  padding: '8px 12px'
                }}>
                    <p style={{
                    color: '#fbbf24',
                    fontWeight: 700,
                    fontSize: '0.82rem',
                    margin: 0
                  }}>
                      💣 Profissional diferenciado
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
        <div className="line-glow absolute bottom-0 left-0 right-0" />
      </section>

      <section className="section-alt relative py-20 sm:py-28 overflow-hidden">
        <MedicalGrid />
        <div style={{
        position: 'absolute',
        inset: 0,
        background: 'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(251,191,36,0.06) 0%, rgba(139,92,246,0.04) 50%, transparent 100%)',
        pointerEvents: 'none'
      }} />
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6">
          <Reveal className="text-center mb-12">
            <div className="section-tag" style={{
            margin: '0 auto 16px',
            display: 'inline-flex',
            background: 'rgba(251,191,36,0.10)',
            borderColor: 'rgba(251,191,36,0.25)',
            color: '#fbbf24'
          }}>
              <Star size={12} style={{
              fill: '#fbbf24'
            }} /> Bônus Exclusivo Incluso
            </div>
            <h2 style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 800,
            fontSize: 'clamp(1.9rem, 3.5vw, 2.8rem)',
            color: '#ffffff',
            letterSpacing: '-0.02em',
            marginBottom: 16
          }}>
              🧠 <span className="text-glow-gold">OncoTreino IA</span>
              <br />
              Plataforma Interativa Inclusa no Pacote
            </h2>
            <p style={{
            color: '#7a6e9a',
            fontSize: '1.05rem',
            lineHeight: 1.75,
            maxWidth: 680,
            margin: '0 auto'
          }}>
              Como bônus exclusivo, você terá acesso ao{' '}
              <strong style={{
              color: '#fbbf24'
            }}>OncoTreino IA</strong>, uma plataforma
              interativa de treinamento em enfermagem oncológica.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div style={{
            background: 'linear-gradient(145deg, #0e0a1f 0%, #09071a 100%)',
            border: '1px solid rgba(251,191,36,0.22)',
            borderRadius: 24,
            overflow: 'hidden',
            boxShadow: '0 0 80px rgba(251,191,36,0.06), 0 0 120px rgba(139,92,246,0.06), 0 32px 80px rgba(0,0,0,0.65)'
          }}>
              <div style={{
              height: 3,
              background: 'linear-gradient(90deg, #fbbf24 0%, #f59e0b 35%, #8b5cf6 100%)'
            }} />

              <div className="grid grid-cols-1 md:grid-cols-2">
                <div style={{
                padding: 'clamp(24px, 5vw, 40px) clamp(18px, 5vw, 36px)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: 24
              }}>
                  <div>
                    <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    background: 'rgba(251,191,36,0.10)',
                    border: '1px solid rgba(251,191,36,0.28)',
                    borderRadius: 99,
                    padding: '5px 14px',
                    marginBottom: 22,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: '#fbbf24',
                    fontFamily: "'Space Grotesk', sans-serif",
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase'
                  }}>
                      <Star size={10} style={{
                      fill: '#fbbf24',
                      color: '#fbbf24'
                    }} />
                      7 Módulos Inclusos
                    </div>

                    <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                    gap: '8px 14px',
                    marginBottom: 24
                  }}>
                      {ONCO_TREINO_MODULES.map(mod => <div key={mod.id} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 8
                    }}>
                          <div style={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        flexShrink: 0,
                        marginTop: 1,
                        background: 'rgba(139,92,246,0.12)',
                        border: '1px solid rgba(139,92,246,0.32)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.65rem'
                      }}>
                            {mod.icon}
                          </div>
                          <span style={{
                        color: '#d4c8f0',
                        fontSize: '0.83rem',
                        lineHeight: 1.4,
                        fontWeight: 500
                      }}>
                            {mod.title}
                          </span>
                        </div>)}
                    </div>
                  </div>

                  <div>
                    <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    marginBottom: 20,
                    padding: '11px 15px',
                    borderRadius: 10,
                    background: 'rgba(251,191,36,0.04)',
                    border: '1px solid rgba(251,191,36,0.14)'
                  }}>
                      <Shield size={13} style={{
                      color: '#fbbf24',
                      flexShrink: 0,
                      marginTop: 2
                    }} />
                      <p style={{
                      color: '#7a6e9a',
                      fontSize: '0.77rem',
                      lineHeight: 1.55,
                      margin: 0
                    }}>
                        Ferramenta educacional baseada em INCA, ESMO e COFEN — não substitui
                        conduta médica.
                      </p>
                    </div>
                    <Link to={ROUTE_PATHS.ONCO_TREINO}>
                      <button className="btn-cta-gold" style={{
                      fontSize: '0.92rem',
                      padding: '13px 24px'
                    }}>
                        <Brain size={16} />
                        Conhecer a Plataforma OncoTreino IA
                      </button>
                    </Link>
                  </div>
                </div>

                <div style={{
                position: 'relative',
                background: '#07050f',
                minHeight: 'clamp(240px, 50vw, 400px)',
                overflow: 'hidden'
              }}>
                  <div style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 1,
                  pointerEvents: 'none',
                  background: 'radial-gradient(ellipse 85% 75% at 60% 50%, rgba(251,191,36,0.07) 0%, rgba(139,92,246,0.06) 55%, transparent 100%)'
                }} />
                  <img src={IMAGES.ONCO_TREINO_BONUS} alt="OncoTreino IA — Plataforma Interativa" style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  objectPosition: 'center',
                  padding: '28px',
                  zIndex: 2,
                  filter: 'drop-shadow(0 0 28px rgba(251,191,36,0.20)) drop-shadow(0 0 56px rgba(139,92,246,0.14))'
                }} />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
        <div className="line-glow absolute bottom-0 left-0 right-0" />
      </section>


      <section id="preco" className="relative py-28 overflow-hidden" style={{
      background: '#07050f'
    }}>
        <MedicalGrid />
        <div style={{
        position: 'absolute',
        top: '30%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 600,
        height: 400,
        background: 'radial-gradient(ellipse, rgba(139,92,246,0.06) 0%, transparent 70%)',
        pointerEvents: 'none'
      }} />

        <div className="relative z-10 max-w-2xl mx-auto px-6">
          <Reveal className="text-center mb-10">
            <div className="section-tag" style={{
            margin: '0 auto 16px',
            display: 'inline-flex'
          }}>
              Oferta especial
            </div>
            <h2 style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 800,
            fontSize: 'clamp(1.8rem, 3vw, 2.5rem)',
            color: '#ffffff',
            letterSpacing: '-0.02em'
          }}>
              Invista na Sua <span className="text-glow-violet">Carreira Hoje</span>
            </h2>
          </Reveal>

          <Reveal delay={0.1}>
            <div style={{
            background: 'linear-gradient(145deg, #100d1c 0%, #0b0814 100%)',
            border: '1px solid rgba(251,191,36,0.25)',
            borderRadius: 20,
            padding: 'clamp(24px, 6vw, 48px)'
          }}>
              <div className="text-center mb-8">
                <div style={{
                color: '#7a6e9a',
                fontSize: '0.85rem',
                fontWeight: 500,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                marginBottom: 8
              }}>
                  Mini Curso
                </div>
                <div style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 800,
                fontSize: '1.6rem',
                color: '#ffffff',
                marginBottom: 24
              }}>
                  Oncologia em Foco
                </div>
                <div className="line-glow mb-8" />

                {/* Bloco de preço principal */}
                <div style={{
                background: 'linear-gradient(135deg, rgba(251,191,36,0.07) 0%, rgba(139,92,246,0.07) 100%)',
                border: '1px solid rgba(251,191,36,0.2)',
                borderRadius: 16,
                padding: 'clamp(20px,5vw,36px)',
                marginBottom: 20,
                position: 'relative',
                overflow: 'hidden'
              }}>
                  {/* glow decorativo */}
                  <div style={{
                  position: 'absolute',
                  top: -40,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 260,
                  height: 120,
                  background: 'radial-gradient(ellipse, rgba(251,191,36,0.18) 0%, transparent 70%)',
                  pointerEvents: 'none'
                }} />

                  <p style={{
                  color: '#a78bfa',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  marginBottom: 10
                }}>
                    Melhor forma de pagamento
                  </p>

                  {/* Parcela — destaque máximo */}
                  <div style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'center',
                  gap: 6,
                  marginBottom: 6
                }}>
                    <span style={{
                    color: '#fbbf24',
                    fontFamily: "'Space Grotesk',sans-serif",
                    fontWeight: 700,
                    fontSize: 'clamp(0.95rem,2vw,1.1rem)'
                  }}>12x de</span>
                    <span style={{
                    fontFamily: "'Space Grotesk',sans-serif",
                    fontWeight: 800,
                    fontSize: 'clamp(2.6rem,8vw,4rem)',
                    lineHeight: 1,
                    color: '#fbbf24',
                    textShadow: '0 0 24px rgba(251,191,36,0.5), 0 0 60px rgba(251,191,36,0.18)'
                  }}>R$&nbsp;20,37</span>
                  </div>

                  <p style={{
                  color: '#7a6e9a',
                  fontSize: '0.82rem',
                  marginBottom: 16
                }}>
                    no cartão sem juros · ou{' '}
                    <span style={{
                    color: '#d4c8ff',
                    fontWeight: 600
                  }}>R$ 197,00 à vista no Pix</span>
                  </p>

                  {/* Pills */}
                  <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  flexWrap: 'wrap'
                }}>
                    <span style={{
                    background: 'rgba(139,92,246,0.15)',
                    border: '1px solid rgba(139,92,246,0.35)',
                    borderRadius: 99,
                    padding: '5px 16px',
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    color: '#c4b5fd'
                  }}>
                      💳 Cartão em 12x sem juros
                    </span>
                    <span style={{
                    background: 'rgba(251,191,36,0.1)',
                    border: '1px solid rgba(251,191,36,0.3)',
                    borderRadius: 99,
                    padding: '5px 16px',
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    color: '#fbbf24'
                  }}>
                      ⚡ Pix à vista
                    </span>
                  </div>
                </div>{/* fim bloco preço */}
              </div>{/* fim text-center mb-8 */}

              <div style={{
              marginBottom: 28
            }} className="space-y-3">
                {['6 aulas completas com profundidade técnica', 'Acesso imediato após a compra', 'Assista no celular, tablet ou computador', 'Certificado digital de conclusão', 'OncoTreino IA: 102 casos + 90 questões', 'Garantia incondicional de 7 dias'].map(item => <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 size={16} style={{
                  color: '#a78bfa',
                  flexShrink: 0
                }} />
                    <span style={{
                  color: '#f0eeff',
                  fontSize: '0.92rem'
                }}>{item}</span>
                  </div>)}
              </div>

              <button className="btn-cta-gold" onClick={openCheckout}>
                <Zap size={20} />
                Quero Garantir Meu Acesso — 12x de R$ 20,37
              </button>

              <div className="flex items-center justify-center gap-3 mt-6">
                <img src={IMAGES.GARANTIA_BADGE} alt="Garantia 7 dias" style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                objectFit: 'cover'
              }} />
              </div>

              <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 20,
              marginTop: 20,
              paddingTop: 20,
              borderTop: '1px solid rgba(255,255,255,0.06)'
            }}>
                {[{
                icon: Lock,
                label: 'Pagamento seguro'
              }, {
                icon: CreditCard,
                label: 'Acesso imediato'
              }, {
                icon: Shield,
                label: 'Garantia de 7 dias'
              }].map(({
                icon: Ic,
                label
              }) => <div key={label} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                color: '#7a6e9a',
                fontSize: '0.8rem'
              }}>
                    <Ic size={14} />
                    {label}
                  </div>)}
              </div>
            </div>
          </Reveal>
        </div>
        <div className="line-glow absolute bottom-0 left-0 right-0" />
      </section>

      <section className="section-alt relative py-20 sm:py-28 overflow-hidden">
        <MedicalGrid />
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6">
          <Reveal className="text-center mb-16">
            <div className="section-tag" style={{
            margin: '0 auto 16px',
            display: 'inline-flex'
          }}>
              Dúvidas frequentes
            </div>
            <h2 style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 800,
            fontSize: 'clamp(1.8rem, 3vw, 2.5rem)',
            color: '#ffffff',
            letterSpacing: '-0.02em'
          }}>
              Perguntas <span className="text-glow-violet">Frequentes</span>
            </h2>
          </Reveal>

          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="space-y-3">
              {FAQ_ITEMS.map(faq => <AccordionItem key={faq.question} value={faq.question} className="card-premium border-0">
                  <AccordionTrigger className="hover:no-underline px-5 py-5 text-left" style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 600,
                color: '#ffffff',
                fontSize: '0.97rem'
              }}>
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-5">
                    <div style={{
                  height: 1,
                  background: 'linear-gradient(90deg, rgba(139,92,246,0.3), transparent)',
                  marginBottom: 14
                }} />
                    <p style={{
                  color: '#7a6e9a',
                  lineHeight: 1.7,
                  fontSize: '0.9rem'
                }}>
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </Reveal>
        </div>
        <div className="line-glow absolute bottom-0 left-0 right-0" />
      </section>

      <footer style={{
      background: '#07050f',
      borderTop: '1px solid #1e1630',
      padding: 'clamp(20px,5vw,32px) clamp(16px,5vw,24px)',
      textAlign: 'center'
    }}>
        <div style={{
        color: '#7a6e9a',
        fontSize: '0.82rem'
      }}>
          © 2026 Oncologia em Foco. Todos os direitos reservados.
        </div>
      </footer>
    </div>;
}